import { Box } from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import * as React from 'react';

import { AsyncContent } from '~/components/AsyncContent';

import { useSxStyles } from './useStyles';

export interface ListItemComponentProps<LI, LICustomActions> {
  listItem: LI;
  className?: string;
  isTabDisabled?: boolean;
  isOldUi?: boolean;
  hasBorder?: boolean;
  handleClose?: (id: string | number) => void;
  handleSecondaryAction?: (id: string | number) => void;
  customActions?: LICustomActions;
}

export interface Props<LI, LICustomActions> {
  listItemsLoading: boolean;
  listItems?: LI[];
  failedToLoadListItems: boolean;
  noDataMessage: string;
  getIdentifier: (item: LI) => string | number;
  onResize?: (noOfFittingListItems: number) => void;
  isTabDisabled?: boolean;
  isOldUi?: boolean;
  hasBorder?: boolean;
  handleClose?: (id: string | number) => void;
  handleSecondaryAction?: (id: string | number) => void;
  customActions?: LICustomActions;
}

export type ContentComponentFactoryPropsType<LI, LICustomActions> = {
  ListItemComponent: React.ComponentType<
    ListItemComponentProps<LI, LICustomActions>
  >;
};

export const ContentComponentFactory = <
  LI extends {},
  LICustomActions extends {},
>(
  props: ContentComponentFactoryPropsType<LI, LICustomActions>,
) => {
  const { ListItemComponent } = props;

  const Component: React.FC<Props<LI, LICustomActions>> = ({
    listItemsLoading,
    listItems,
    failedToLoadListItems,
    noDataMessage,
    getIdentifier,
    onResize,
    isTabDisabled,
    isOldUi,
    hasBorder,
    handleClose,
    handleSecondaryAction,
    customActions,
  }) => {
    const sxStyles = useSxStyles();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const { height: containerHeight } = useComponentSize(containerRef);

    const noOfFittingListItems = containerHeight / 48;

    React.useEffect(() => {
      onResize && onResize(noOfFittingListItems);
    }, [noOfFittingListItems, onResize]);

    return (
      <Box component="span" ref={containerRef} sx={sxStyles.refDiv}>
        <AsyncContent
          isLoading={listItemsLoading}
          failedToLoad={failedToLoadListItems}
          noData={!listItems || !listItems.length}
          noDataMessage={noDataMessage}
          customSxStyles={{ errorMessage: sxStyles.contentPadding }}
        >
          <Box
            data-cy="list"
            sx={!!hasBorder ? sxStyles.listContainer : undefined}
          >
            {listItems?.map((listItem) => (
              <Box
                sx={!hasBorder ? sxStyles.listItemContainer : undefined}
                key={getIdentifier(listItem)}
                data-cy="list-item"
              >
                <ListItemComponent
                  listItem={listItem}
                  isTabDisabled={isTabDisabled}
                  isOldUi={isOldUi}
                  hasBorder={hasBorder}
                  handleClose={handleClose}
                  handleSecondaryAction={handleSecondaryAction}
                  customActions={customActions}
                />
              </Box>
            ))}
          </Box>
        </AsyncContent>
      </Box>
    );
  };

  return Component;
};
